"use client"

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BellRing, Menu, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RoleBadge } from '@/components/ui/role-badge'
import { User } from '@/types'
import { notificationsApi } from '@/lib/app-api'
import { resolveNotificationLink } from '@/lib/notification-routing'
import { formatRelativeTime } from '@/lib/utils'

interface AppHeaderProps {
  onMenuClick: () => void
  user: User
  onLogout: () => void
}

export function AppHeader({ onMenuClick, user, onLogout }: AppHeaderProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false)

  const { data: notificationsResponse, refetch: refetchNotifications } = useQuery({
    queryKey: ['header-notifications'],
    queryFn: () => notificationsApi.getNotifications({ page: 1, limit: 8 }),
    staleTime: 30000,
  })

  const notifications = notificationsResponse?.data || []
  const unreadCount = notifications.filter((n) => !n.read).length

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['header-notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const handleNotificationClick = async (notification: any) => {
    if (!notification?.read) {
      try {
        await markAsReadMutation.mutateAsync(notification.id)
      } catch {
        // Do not block navigation on read-state update failures.
      }
    }
    router.push(resolveNotificationLink(notification))
  }

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
      {/* Left side - Menu button and search */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search projects, tasks..."
            className="w-64 pl-9"
          />
        </div>
      </div>

      {/* Right side - Notifications and user menu */}
      <div className="flex items-center space-x-2">
        {/* Notifications */}
        <DropdownMenu
          open={isNotificationMenuOpen}
          onOpenChange={(open) => {
            setIsNotificationMenuOpen(open)
            if (open) {
              // Force a fresh fetch when opening notifications so users
              // always see recent items and corresponding network activity.
              void refetchNotifications()
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 rounded-full"
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
            >
              <BellRing className="h-4 w-4 text-foreground" strokeWidth={2.2} />
              {unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center rounded-full border-2 border-background text-[10px] leading-none"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-96" align="end">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && <Badge variant="secondary">{unreadCount} unread</Badge>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 ? (
              <DropdownMenuItem disabled>No notifications</DropdownMenuItem>
            ) : (
              notifications.slice(0, 5).map((notification: any) => (
                <DropdownMenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="cursor-pointer flex flex-col items-start gap-1 py-2"
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <span className={`text-sm ${notification.read ? 'font-normal' : 'font-semibold'}`}>
                      {notification.title}
                    </span>
                    {!notification.read && <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className="line-clamp-2 text-xs text-muted-foreground">{notification.message}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatRelativeTime(notification.createdAt)}
                  </span>
                </DropdownMenuItem>
              ))
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/notifications" className="w-full cursor-pointer">
                View All Notifications
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user.avatar} alt={user.name || 'User'} />
                <AvatarFallback>
                  {(user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user.email}
                </p>
                <RoleBadge role={user.role} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuItem>
              Preferences
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
